import { useCallback, useEffect, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";

const POLL_MS = 5000;
const RUNNING = new Set(["queued", "running"]);

/**
 * The latest import (`GET /api/crm/imports/latest`, `null` when the practice never
 * imported) for the Clients banner: progress while it runs (polled every 5 s), then the
 * result summary until someone dismisses it.
 *
 * Dismissal is the SERVER's (contract v1.5 CR-F-6): `POST /api/crm/imports/{id}/dismiss`
 * stamps `banner_dismissed_at` on the job, so a dismissed banner stays dismissed on every
 * device and for every team member. The banner hides at once and comes back if the
 * server refuses.
 */
export default function useActiveImport() {
  const [job, setJob] = useState(null);
  const load = useCallback(async () => { setJob((await crmApi.getLatestImport()) || null); }, []);
  const { loading, loadError, reload } = useAdminLoad(load);

  const running = !!job && RUNNING.has(job.status);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => { load().catch(() => {}); }, POLL_MS);
    return () => clearInterval(id);
  }, [running, load]);

  const dismiss = useCallback(async () => {
    if (!job) return;
    const before = job;
    setJob({ ...job, banner_dismissed_at: new Date().toISOString() });
    try {
      const saved = await crmApi.dismissImport(before.id);
      if (saved && typeof saved === "object") setJob(saved);
    } catch (e) {
      setJob(before);
      throw e;
    }
  }, [job]);

  const visible = !!job && !job.banner_dismissed_at && (running || job.status === "completed" || job.status === "failed");
  return { job: visible ? job : null, running, loading, loadError, reload, dismiss };
}
